import { useEffect, useMemo, useRef, useState } from 'react'
import { Sparkles, Send, GitBranch, Check, RotateCcw } from 'lucide-react'
import type { FitThesis, FitThesisChatMessage } from '@ispot/shared'
import { MarkdownContent, extractPills } from '@/components/hermes/SkerpaMessageContent'

interface ChatLike {
  messages: FitThesisChatMessage[]
  streaming: boolean
  error: string | null
  complete: boolean
  sendMessage(m: string): Promise<void>
  retry?(): Promise<void>
}

interface Props {
  chat: ChatLike
  thesis: FitThesis
  personaName: string | null
  jtbdLabel: string
}

const assistantAvatarStyle = {
  background: 'var(--color-accent-50)',
  color: 'var(--color-accent-500)',
}

export function FitThesisChat({ chat, thesis, personaName, jtbdLabel }: Props) {
  const [input, setInput] = useState('')
  const listRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const kickedRef = useRef(false)

  useEffect(() => {
    listRef.current?.scrollTo({ top: listRef.current.scrollHeight, behavior: 'smooth' })
  }, [chat.messages, chat.streaming])

  // On first open with an empty transcript, ask the coach to proactively begin.
  // Empty userMessage = "kick" on the backend. Nothing is persisted as a user turn;
  // the coach just opens with the first question about the next empty block.
  useEffect(() => {
    if (kickedRef.current) return
    if (chat.messages.length === 0 && !chat.streaming && !chat.error) {
      kickedRef.current = true
      void chat.sendMessage('')
    }
  }, [chat.messages.length, chat.streaming, chat.error, chat])

  // Extract clickable follow-up chips from the trailing assistant message.
  const pills = useMemo(() => {
    if (chat.streaming) return []
    for (let i = chat.messages.length - 1; i >= 0; i--) {
      if (chat.messages[i].role === 'assistant') return extractPills(chat.messages[i].content)
    }
    return []
  }, [chat.messages, chat.streaming])

  function autoGrow() {
    const el = textareaRef.current
    if (!el) return
    el.style.height = 'auto'
    el.style.height = Math.min(el.scrollHeight, 140) + 'px'
  }

  async function handleSend(text?: string) {
    const msg = (text ?? input).trim()
    if (!msg || chat.streaming) return
    setInput('')
    autoGrow()
    await chat.sendMessage(msg)
  }

  function handleKey(e: React.KeyboardEvent) {
    if ((e.key === 'Enter' && (e.metaKey || e.ctrlKey)) || (e.key === 'Enter' && !e.shiftKey)) {
      e.preventDefault()
      handleSend()
    }
  }

  // Silence unused-warning. Kept in Props for future context display.
  void thesis; void personaName; void jtbdLabel

  return (
    <section className="flex h-full min-h-0 flex-col border-r border-border bg-bg max-[900px]:border-r-0">
      <div className="flex items-center gap-2 border-b border-border bg-bg px-7 py-3 text-caption text-fg-secondary max-sm:px-5">
        <Sparkles className="h-3.5 w-3.5 text-accent-500" />
        <span>
          Guided thesis session. Answers save into the 9-block rail.
        </span>
      </div>

      <div ref={listRef} className="flex-1 space-y-4 overflow-y-auto px-7 py-6 max-sm:px-5">
        {chat.messages.length === 0 && !chat.streaming && !chat.error && <TypingBubble />}

        {chat.messages.map(m => (
          <Message key={m.id} m={m} />
        ))}

        {chat.streaming && chat.messages[chat.messages.length - 1]?.role !== 'assistant' && (
          <TypingBubble />
        )}

        {pills.length > 0 && !chat.streaming && (
          <div className="flex flex-wrap gap-1.5 pt-1 pl-8">
            {pills.map((pill, i) => (
              <button
                key={i}
                onClick={() => handleSend(pill.prompt)}
                className="rounded-full bg-bg-secondary px-2.5 py-1 text-caption text-fg-secondary leading-tight transition-colors hover:bg-border"
              >
                {pill.label}
              </button>
            ))}
          </div>
        )}

        {chat.error && (
          <div className="flex items-start justify-between gap-3 rounded-md border border-danger-fg bg-danger-bg px-3 py-2 text-body text-danger-fg">
            <span className="flex-1 min-w-0">{chat.error}</span>
            {chat.retry && (
              <button
                onClick={() => chat.retry?.()}
                disabled={chat.streaming}
                className="inline-flex items-center gap-1 rounded-md border border-danger-fg/40 bg-bg-card px-2 py-0.5 text-caption font-medium transition hover:bg-danger-bg disabled:opacity-50"
              >
                <RotateCcw className="h-3 w-3" /> Retry
              </button>
            )}
          </div>
        )}

        {chat.complete && (
          <div className="inline-flex items-center gap-2 rounded-md border bg-success-bg px-3 py-2 text-body text-success-fg" style={{ borderColor: 'var(--color-success-fg)' }}>
            <Check className="h-3.5 w-3.5" />
            All nine blocks stand up. Validate in the header when you&rsquo;re ready.
          </div>
        )}
      </div>

      <div className="border-t border-border bg-bg-card px-7 py-4 max-sm:px-5">
        <div className="flex items-end gap-2 rounded-lg border border-border bg-bg-card p-2.5 focus-within:border-accent-500 focus-within:ring-2 focus-within:ring-accent-500/15">
          <textarea
            ref={textareaRef}
            rows={1}
            value={input}
            onChange={e => { setInput(e.target.value); autoGrow() }}
            onKeyDown={handleKey}
            placeholder="Reply to Skerpa..."
            className="flex-1 resize-none bg-transparent text-body text-fg outline-none placeholder:text-fg-muted"
          />
          <button
            onClick={() => handleSend()}
            disabled={chat.streaming || !input.trim()}
            className="flex h-8 w-8 items-center justify-center rounded-md bg-accent-500 text-bg-card transition hover:bg-accent-600 disabled:bg-border-strong disabled:opacity-60"
            title="Send message"
          >
            <Send className="h-3.5 w-3.5" />
          </button>
        </div>
        <div className="mt-2 flex items-center justify-between text-caption text-fg-muted">
          <span>Enter to send. Shift+Enter for newline.</span>
          <span className="inline-flex items-center gap-1">
            <GitBranch className="h-3 w-3" /> Conversation saved to activity
          </span>
        </div>
      </div>
    </section>
  )
}

function Message({ m }: { m: FitThesisChatMessage }) {
  if (m.role === 'assistant') {
    return (
      <div className="flex gap-2">
        <div className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full" style={assistantAvatarStyle}>
          <Sparkles className="h-3 w-3" />
        </div>
        <div className="min-w-0 flex-1">
          <div className="mb-1 text-[11px] font-semibold text-fg">Skerpa</div>
          <div className="max-w-[740px] rounded-md border border-border bg-bg-card px-3.5 py-3">
            {m.content
              ? <MarkdownContent content={m.content} />
              : <span className="text-body italic text-fg-muted">...</span>}
          </div>
        </div>
      </div>
    )
  }

  const isDirect = Boolean(m.metadata?.direct)
  return (
    <div className="flex justify-end gap-2">
      <div className="min-w-0 max-w-[420px]">
        <div className="mb-1 text-right text-[11px] font-semibold text-fg">You</div>
        <div
          className="rounded-md rounded-tr-sm px-3.5 py-2 text-body leading-relaxed whitespace-pre-wrap break-words"
          style={isDirect
            ? { border: '1px dashed var(--color-border-strong)', background: 'var(--color-bg-secondary)', color: 'var(--color-fg-secondary)' }
            : { background: 'var(--color-primary-500)', color: 'var(--color-bg-card)' }}
        >
          {isDirect && <span className="mr-1.5 text-caption font-semibold text-fg-muted">direct edit:</span>}
          {m.content}
        </div>
      </div>
      <div className="mt-5 flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full bg-primary-500 text-caption font-semibold text-bg-card">
        Y
      </div>
    </div>
  )
}

function TypingBubble() {
  return (
    <div className="flex gap-2">
      <div className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full" style={assistantAvatarStyle}>
        <Sparkles className="h-3 w-3" />
      </div>
      <div className="flex items-center gap-1 py-1.5">
        <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-fg-muted" />
        <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-fg-muted" style={{ animationDelay: '150ms' }} />
        <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-fg-muted" style={{ animationDelay: '300ms' }} />
      </div>
    </div>
  )
}
